import * as PIXI from 'pixi.js';
import { Vector2 } from '../core/Vector2';

export interface InputState {
  move: Vector2;
  attack: boolean;
  interact: boolean;
  pause: boolean;
}

const DRAG_THRESHOLD = 10;

export class InputSystem {
  private keys = new Set<string>();
  private moveInput = new Vector2();
  private attackPressed = false;
  private interactPressed = false;
  private pausePressed = false;

  private app: PIXI.Application | null = null;
  private attackButton: PIXI.Container | null = null;

  // Drag tracking
  private dragStart: { x: number; y: number } | null = null;

  constructor(_stage: PIXI.Container) {
    this.setupKeyboard();
  }

  setApp(app: PIXI.Application): void {
    this.app = app;
  }

  setupVirtualJoystick(): void {
    if (!this.app) return;

    const canvas = this.app.canvas as HTMLCanvasElement;
    if (!canvas) return;

    // --- Full-screen drag control ---
    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      // Ignore if hitting attack button
      if (this.attackButton) {
        const bounds = this.attackButton.getBounds();
        if (
          e.clientX >= bounds.x &&
          e.clientX <= bounds.x + bounds.width &&
          e.clientY >= bounds.y &&
          e.clientY <= bounds.y + bounds.height
        ) {
          return;
        }
      }

      this.dragStart = { x: e.clientX, y: e.clientY };
    });

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (!this.dragStart) return;

      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > DRAG_THRESHOLD) {
        // Normalize direction
        this.moveInput.set(dx / dist, dy / dist);
      } else {
        this.moveInput.set(0, 0);
      }
    });

    const endDrag = () => {
      this.dragStart = null;
      this.moveInput.set(0, 0);
    };

    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('pointerleave', endDrag);

    // --- Attack button (bottom-right) ---
    this.attackButton = new PIXI.Container();
    this.attackButton.eventMode = 'static';
    this.attackButton.cursor = 'pointer';

    const atkBg = new PIXI.Graphics();
    atkBg.circle(0, 0, 36);
    atkBg.fill({ color: 0xff4444, alpha: 0.35 });
    atkBg.stroke({ color: 0xff4444, width: 2, alpha: 0.5 });
    this.attackButton.addChild(atkBg);

    const atkText = new PIXI.Text({
      text: 'ATK',
      style: { fill: 0xffffff, fontSize: 14, fontFamily: 'Arial', fontWeight: 'bold' },
    });
    atkText.anchor.set(0.5);
    this.attackButton.addChild(atkText);

    const w = this.app.screen.width;
    const h = this.app.screen.height;
    this.attackButton.x = w - 80 - 36;
    this.attackButton.y = h - 80 - 36;

    this.attackButton.on('pointerdown', () => { this.attackPressed = true; });
    this.attackButton.on('pointerup', () => { this.attackPressed = false; });
    this.attackButton.on('pointerupoutside', () => { this.attackPressed = false; });

    this.app.stage.addChild(this.attackButton);
  }

  private setupKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'Space') this.attackPressed = true;
      if (e.code === 'KeyE') this.interactPressed = true;
      if (e.code === 'Escape') this.pausePressed = true;
    });

    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      if (e.code === 'Space') this.attackPressed = false;
      if (e.code === 'KeyE') this.interactPressed = false;
      if (e.code === 'Escape') this.pausePressed = false;
    });
  }

  getState(): InputState {
    // Keyboard override
    const kbMove = new Vector2();
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) kbMove.y = -1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) kbMove.y = 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) kbMove.x = -1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) kbMove.x = 1;

    if (kbMove.lengthSq() > 0) {
      kbMove.normalize();
      return {
        move: kbMove,
        attack: this.keys.has('Space'),
        interact: this.keys.has('KeyE'),
        pause: this.keys.has('Escape'),
      };
    }

    return {
      move: this.moveInput.clone(),
      attack: this.attackPressed,
      interact: this.interactPressed,
      pause: this.pausePressed,
    };
  }

  repositionButtons(w: number, h: number): void {
    if (this.attackButton) {
      this.attackButton.x = w - 80 - 36;
      this.attackButton.y = h - 80 - 36;
    }
  }

  destroy(): void {
    this.keys.clear();
    this.attackButton?.destroy({ children: true });
  }
}
